import { ColDef, EditableCallbackParams, ICellEditorParams, ICellRendererParams } from 'ag-grid-community';
import { addDays, isBefore, startOfDay } from 'date-fns';
import { get, orderBy } from 'lodash';
import React from 'react';
import { createSelector } from 'reselect';
import { AgreementCell, GuardedColDef, PriceCell } from '../../../common/datagrid';
import { AgHeaderComponent } from '../../../common/datagrid/components/AgHeaderComponent';
import { ExternalPricelistDto, PriceChangeDirection, ResourceType } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import {
    canSeeStockColumn,
    includes,
    notNull,
    roundTwoDecimals,
    startsWith,
    toDateString,
    toLowerCase
} from '../../../utils';
import {
    getExternalPricelists,
    getUnfilteredExternalPricelists
} from '../../externalpricelist/redux/externalPricelistSelectors';
import { getUnfilteredPricelists } from '../../pricelist/redux/pricelistSelectors';
import { getProducts } from '../../product/redux/productSelectors';
import { getCurrentUserPermissions } from '../../user/redux/userSelectors';
import { ExternalPriceRow } from '../externalPriceTypes';
import {
    getExternalPriceKey,
    saveExternalPriceComment,
    saveExternalPriceDiscount
} from './externalPriceSlices';

export const getSelectedExternalPricelistIds = (state: RootState) =>
    state.externalPrice.selectedExternalPricelistIds;
export const getExternalPricelistPrices = (state: RootState) => state.externalPrice.externalPrices;
export const getIsExternalPriceTableLoading = (state: RootState) => state.externalPrice.ui.isLoading;
export const getExternalPriceTable = (state: RootState) => state.externalPrice.ui.externalPriceTable;
export const getExternalPriceTableVisibleColumnKeys = createSelector(
    [(state: RootState) => state.externalPrice.ui.externalPriceTable.columns],
    cols => cols.filter(c => !c.hide)
);
export const getIsChooseExternalPricelistsDialogOpen = (state: RootState) =>
    state.externalPrice.ui.externalPricelistDialog.isChooseExternalPricelistsDialogOpen;
export const getCheckboxFilters = (state: RootState) => state.externalPrice.ui.checkboxFilters;
export const getSelectedExternalPricelistDate = (state: RootState) =>
    state.externalPrice.selectedExternalPriceDate;
export const getSelectedPricelistPeriods = (state: RootState) =>
    state.externalPrice.selectedExternalPricelistPeriods;
export const getExternalPriceSource = (state: RootState) => state.externalPrice.externalPriceSource;

// const commonColumnProps: Partial<Column<ExternalPriceRow>> = {
//     filterRenderer: DefaultFilterRenderer,
//     resizable: true
// };
const commonColumnProps: Partial<ColDef<ExternalPriceRow>> = {
    // filterRenderer: DefaultFilterRenderer,
    resizable: true
};
const columns = [
    {
        field: 'productId',
        headerName: 'Product Id',
        width: 100,
        sortable: true,
        cellRenderer: ({ data: row }: ICellEditorParams<ExternalPriceRow>) =>
            AgreementCell({
                value: String(row.productId),
                agreementPriceIds: row.agreementPriceIds,
                priceType: 'ext'
            }),
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        }
    },
    {
        field: 'productExtRef',
        headerName: 'Ref',
        width: 100,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        },
        sortable: true
    },
    {
        field: 'productName',
        headerName: 'Product name',
        width: 300,
        sortable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        }
    },
    {
        field: 'unit',
        headerName: 'Unit',
        width: 80,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        }
    },
    {
        field: 'country',
        headerName: 'Country',
        width: 82,
        sortable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: {
            suppressFilterButton: true
        }
    }
] as ColDef<ExternalPriceRow>[];

export const getColumnDefs = createSelector(
    [
        getSelectedExternalPricelistIds,
        getExternalPricelists,
        getExternalPriceTableVisibleColumnKeys,
        getSelectedExternalPricelistDate,
        getCurrentUserPermissions,
        getUnfilteredPricelists,
        getProducts,
        getExternalPriceTable
    ],
    (
        selectedExternalPricelistIds,
        externalPricelists,
        externalPriceTableColumnKeys,
        selectedExternalPricelistDate,
        currentUserPermissions,
        pricelists,
        products,
        externalPriceTable
    ) => {
        const earliestDateOfAllowedEditing = addDays(startOfDay(new Date()), 1);
        const isDateAllowedForEdit =
            selectedExternalPricelistDate == null
                ? false
                : !isBefore(new Date(selectedExternalPricelistDate), earliestDateOfAllowedEditing);

        const pricelistPermissions = currentUserPermissions.recordPermissions.find(
            x => x.resourceType === ResourceType.ExternalPricelist
        );

        const hasPermissionToEdit = (plId: number) => {
            if (currentUserPermissions.isAdmin) {
                return true;
            }

            return (
                pricelistPermissions?.recordPermissions.some(x => x.recordId === plId && x.canUpdate) ?? false
            );
        };

        const getColumnName = (externalPricelist: ExternalPricelistDto) => {
            return `UT: ${externalPricelist.name}`;
        };

        // indexOfColorProperty represents the index of the property to use in the current applicable palette color in the theme
        const createExternalPricelistColumn = (pl: ExternalPricelistDto, indexOfColorProperty: number) => {
            const hasWritePermission = hasPermissionToEdit(pl.externalPricelistId);
            const readonlyReasons: string[] = [];
            if (!hasWritePermission) {
                readonlyReasons.push(
                    "You don't have permission to edit external prices on this external pricelist"
                );
            }

            if (!isDateAllowedForEdit) {
                readonlyReasons.push(
                    `Not allowed to edit external prices before ${toDateString(
                        earliestDateOfAllowedEditing
                    )}.`
                );
            }

            return {
                field: String(pl.externalPricelistId),
                headerName: getColumnName(pl),
                headerComponent: AgHeaderComponent,
                headerComponentParams: { readonlyReasons, indexOfBadgeColor: indexOfColorProperty },
                cellDataType: 'text',
                floatingFilter: true,
                filter: 'agTextColumnFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                },
                cellRenderer: ({
                    api,
                    data: row,
                    column,
                    node,
                    columnApi,
                    colDef,
                    context
                }: ICellRendererParams) => {
                    const externalPricelistId = Number(column?.getColId());
                    const updatedDateStr = row.updatedDates.externalPrices[externalPricelistId];
                    const priceToBeSaved = row.saves[externalPricelistId];

                    let editable = false;
                    if (typeof colDef?.editable === 'function' && column != null) {
                        editable = colDef.editable({
                            api,
                            column,
                            data: row,
                            node,
                            colDef,
                            columnApi,
                            context
                        });
                    } else {
                        editable = (column?.getColDef().editable ?? false) && hasWritePermission;
                    }

                    return PriceCell({
                        priceId: row.externalPriceIds[externalPricelistId],
                        price: priceToBeSaved ?? row[externalPricelistId],
                        priceChangeDirection: row.priceChangeDirections.external[externalPricelistId],
                        updatedBy: row.updatedBys.externalPrices[externalPricelistId],
                        updatedDate: updatedDateStr != null ? new Date(updatedDateStr) : undefined,
                        commentOpts: {
                            commentable: true,
                            comment: row.comments[externalPricelistId],
                            saveCommentActionCreator: (extPriceId, comment) =>
                                saveExternalPriceComment.request({ extPriceId, comment }),
                            canWrite: editable
                        },
                        discountOpts: {
                            discountable: true,
                            canWrite: editable,
                            discount: row.discounts[externalPricelistId],
                            saveDiscountActionCreator: (extPriceId, discount) =>
                                saveExternalPriceDiscount.request({ extPriceId, discount })
                        },
                        showTooltips: externalPriceTable.showTooltips,
                        isSaving: priceToBeSaved != null,
                        defaultBgColor: editable ? undefined : 'rgba(244, 244, 244, .6)',
                        defaultColor: editable ? undefined : 'rgba(183,183,183, 0.3)'
                    });
                },
                sortable: true,
                editable: (params: EditableCallbackParams<ExternalPriceRow>) =>
                    (isDateAllowedForEdit &&
                        hasWritePermission &&
                        params.data?.externalPriceIds[pl.externalPricelistId] != null) ??
                    false
            } as GuardedColDef<ExternalPriceRow>;
        };

        const createPricelistSourceColumn = (pl: ExternalPricelistDto) => {
            if (pl.pricelistId == null) {
                return null;
            }
            const pricelist = pricelists[pl.pricelistId];
            if (pricelist == null) {
                return null;
            }

            return {
                field: String('inPrice-' + pricelist.pricelistId),
                headerName: `IN: ${pricelist.name}`,
                floatingFilter: true,
                headerComponent: AgHeaderComponent,
                filter: 'agTextColumnFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                },
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    return (
                        (nodeA.data?.inPrices[pricelist.pricelistId] ?? 0) -
                        (nodeB.data?.inPrices[pricelist.pricelistId] ?? 0)
                    );
                },
                cellRenderer: ({ data: row }: ICellRendererParams<ExternalPriceRow>) => {
                    if (row == null) {
                        return React.Fragment;
                    }
                    return PriceCell({
                        price: row.inPrices[pricelist.pricelistId],
                        priceChangeDirection: row.priceChangeDirections.in[pricelist.pricelistId],
                        updatedBy: row.updatedBys.inPrices[pricelist.pricelistId],
                        updatedDate:
                            row.updatedDates.inPrices[pricelist.pricelistId] != null
                                ? new Date(row.updatedDates.inPrices[pricelist.pricelistId])
                                : undefined,
                        styles:
                            row.appliedCalculations.includes(pricelist.pricelistId) ||
                            row.inPrices[pricelist.pricelistId] == null
                                ? undefined
                                : {
                                      textDecoration: 'underline 1px solid'
                                  },
                        showTooltips: externalPriceTable.showTooltips
                    });
                },
                sortable: true,
                editable: false
            } as ColDef<ExternalPriceRow>;
        };

        const calculateMarginExcludingVat = (
            inPrice: number,
            externalPrice: number,
            discount: number | null
        ) => {
            if (inPrice == null) {
                return 0;
            }
            if (externalPrice === 0) {
                return -Infinity;
            }

            if (discount != null) {
                externalPrice = externalPrice - discount;
            }

            return (
                Math.round((((externalPrice - inPrice) / externalPrice) * 100 + Number.EPSILON) * 100) / 100
            );
        };

        const calculateMarginIncludingVat = (
            inPrice: number,
            externalPrice: number,
            discount: number | null,
            vat: number
        ) => {
            const vatPercent = 1 + vat / 100;
            if (externalPrice === 0) {
                return -Infinity;
            }
            if (vat === 0) {
                return calculateMarginExcludingVat(inPrice, externalPrice, discount);
            }
            const externalPriceExVat = (externalPrice - (discount ?? 0)) / vatPercent;
            const result =
                // Difference of (outprice - inprice) / inprice
                Math.round(
                    (((externalPriceExVat - inPrice) / externalPriceExVat) * 100 + Number.EPSILON) * 100
                ) / 100;
            return result;
        };
        const calculateMargin = (pl: ExternalPricelistDto, row?: ExternalPriceRow) => {
            const pricelist = pricelists[pl.pricelistId!];
            if (row != null) {
                if (pl.includeVat) {
                    return calculateMarginIncludingVat(
                        row.inPrices[pricelist.pricelistId],
                        row[pl.externalPricelistId],
                        row.discounts[pl.externalPricelistId],
                        products[row.productId].vat ?? 0
                    );
                } else {
                    return calculateMarginExcludingVat(
                        row.inPrices[pricelist.pricelistId],
                        row[pl.externalPricelistId],
                        row.discounts[pl.externalPricelistId]
                    );
                }
            }
            return 0;
        };
        const createPricelistMarginColumn = (pl: ExternalPricelistDto, indexOfBadgeColor: number) => {
            if (pl.pricelistId == null) {
                return null;
            }
            return {
                field: String('margin-' + pl.externalPricelistId),
                headerName: 'Marginal: ' + pl.name,
                headerComponent: AgHeaderComponent,
                headerComponentParams: { indexOfBadgeColor },
                floatingFilter: true,
                filter: 'agTextColumnFilter',
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                },
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const marginA = calculateMargin(pl, nodeA.data);
                    const marginB = calculateMargin(pl, nodeB.data);
                    return marginA - marginB;
                },

                cellRenderer: ({ data: row }: ICellRendererParams<ExternalPriceRow>) => {
                    let margin = 0;
                    if (row != null) {
                        margin = calculateMargin(pl, row);

                        const priceCell = {
                            price: margin,
                            priceChangeDirection:
                                margin < pl.marginThreshold && isFinite(margin)
                                    ? PriceChangeDirection.Up
                                    : PriceChangeDirection.Unaffected,
                            priceStringSuffix: '%',
                            showTooltips: externalPriceTable.showTooltips
                        };

                        return PriceCell(priceCell);
                    }
                    return React.Fragment;
                },
                sortable: true,
                editable: false
            } as GuardedColDef<ExternalPriceRow>;
        };

        const cols = [
            ...columns.filter(colDef => canSeeStockColumn(colDef.field, currentUserPermissions)),
            ...selectedExternalPricelistIds
                .map(id => externalPricelists[id])
                .filter(notNull)
                .flatMap((pl, index) => [
                    createPricelistSourceColumn(pl),
                    createExternalPricelistColumn(pl, index),
                    createPricelistMarginColumn(pl, index)
                ])
                .filter(notNull)
        ].map(column => ({
            ...column,
            ...commonColumnProps
        }));

        return externalPriceTableColumnKeys
            .map(columnSetting => {
                const column = cols.find(col => col.field === columnSetting.key);
                if (column == null) {
                    return null;
                }

                return { ...column, width: columnSetting.width ?? column.width };
            })
            .filter(notNull) as ColDef<ExternalPriceRow>[];
    }
);

export const getExternalPriceRows = createSelector(
    [
        getProducts,
        getSelectedExternalPricelistIds,
        getExternalPricelistPrices,
        getExternalPriceTable,
        getExternalPriceSource,
        getCheckboxFilters,
        getUnfilteredExternalPricelists
    ],
    (
        products,
        selectedExternalPricelistIds,
        externalPrices,
        { sorting, filters },
        inprices,
        checkboxFilters,
        extPricelists
    ) => {
        const isFilteringPrices = selectedExternalPricelistIds
            .map(id => filters[id])
            .some(f => f != null && f !== '');

        const externalPriceRows = Object.values(products)
            .filter(
                p =>
                    selectedExternalPricelistIds.length > 0 &&
                    startsWith(filters.productId, p.productId.toString()) &&
                    startsWith(filters.productExtRef, p.externalRef) &&
                    includes(filters.productName, p.name) &&
                    includes(filters.unit, p.priceUnit) &&
                    includes(filters.country, p.countryOfOrigin)
            )
            .map(product => {
                const priceRow: ExternalPriceRow = {
                    productId: product.productId,
                    productExtRef: product.externalRef ?? '',
                    productName: product.name,
                    unit: product.priceUnit ?? '',
                    country: product.countryOfOrigin ?? '',
                    inPrices: {},
                    updatedBys: { externalPrices: {}, inPrices: {} },
                    updatedDates: { externalPrices: {}, inPrices: {} },
                    priceChangeDirections: { external: {}, in: {} },
                    externalPriceIds: {},
                    comments: {},
                    discounts: {},
                    saves: {},
                    agreementPriceIds: [],
                    appliedCalculations: []
                };

                for (const externalPricelistId of selectedExternalPricelistIds) {
                    const key = getExternalPriceKey({
                        externalPricelistId: externalPricelistId,
                        productId: product.productId
                    });
                    const externalPrice = externalPrices[key];

                    if (externalPrice == null) {
                        continue;
                    }
                    const inprice = inprices[externalPrice.priceId];
                    if (inprice == null) {
                        continue;
                    }
                    if (
                        !checkboxFilters.showAllArticles &&
                        inprice.priceChangeDirection === PriceChangeDirection.Unaffected
                    ) {
                        continue;
                    }

                    priceRow.agreementPriceIds.push(...externalPrice.agreementPriceIds);
                    priceRow[externalPricelistId] = roundTwoDecimals(externalPrice.salesPrice);
                    priceRow.inPrices[inprice.pricelistId] = inprice.price;
                    priceRow.priceChangeDirections.external[externalPrice.externalPricelistId] =
                        externalPrice.priceChangeDirection;
                    priceRow.priceChangeDirections.in[inprice.pricelistId!] = inprice.priceChangeDirection;

                    if (inprice.updatedBy != null) {
                        priceRow.updatedBys.inPrices[inprice.pricelistId] = inprice.updatedBy;
                    }
                    if (inprice.updatedDate != null) {
                        priceRow.updatedDates.inPrices[inprice.pricelistId!] = inprice.updatedDate;
                    }
                    if (inprice.appliedCalculations) {
                        priceRow.appliedCalculations.push(inprice.pricelistId!);
                    }

                    if (externalPrice.updatedBy != null) {
                        priceRow.updatedBys.externalPrices[externalPricelistId] = externalPrice.updatedBy;
                    }
                    if (externalPrice.updatedDate != null) {
                        priceRow.updatedDates.externalPrices[externalPricelistId] = externalPrice.updatedDate;
                    }
                    priceRow.externalPriceIds[externalPricelistId] = externalPrice.externalPriceId;
                    if (externalPrice.comment != null) {
                        priceRow.comments[externalPricelistId] = externalPrice.comment;
                    }
                    if (externalPrice.discount != null) {
                        priceRow.discounts[externalPricelistId] = externalPrice.discount;
                    }
                    if (externalPrice.priceToBeSaved != null) {
                        priceRow.saves[externalPricelistId] = externalPrice.priceToBeSaved;
                    }
                }

                return Object.keys(priceRow.inPrices).length > 0 ? priceRow : null;
            })
            .filter(notNull)
            .filter(priceRow => {
                if (checkboxFilters.showCommentsOnly && Object.keys(priceRow.comments).length === 0) {
                    return false;
                }
                if (checkboxFilters.showNewArticlesOnly) {
                    return selectedExternalPricelistIds
                        .map(id => priceRow.priceChangeDirections.external[id])
                        .some(pc => pc != null && pc === PriceChangeDirection.NewArticle);
                }
                if (selectedExternalPricelistIds.length === 0 || !isFilteringPrices) {
                    return true;
                }

                if (!isFilteringPrices) {
                    return selectedExternalPricelistIds.map(id => priceRow[id]).some(pr => pr != null);
                }

                return selectedExternalPricelistIds
                    .flatMap(id => {
                        const arr = [[filters[id] as string, priceRow[id]] as const];

                        const extPricelist = extPricelists[id];
                        if (extPricelist != null && extPricelist.pricelistId != null) {
                            const inPlIdKey = 'inPrice-' + extPricelist.pricelistId;
                            arr.push([
                                filters[inPlIdKey] as string,
                                priceRow.inPrices[extPricelist.pricelistId]
                            ] as const);
                        }

                        return arr;
                    })
                    .every(([filter, price]) => startsWith(filter, price?.toString()));
            });

        const nullOrderPlacement = sorting.direction === 'asc' ? Infinity : -Infinity;

        let sortCol = sorting.column.replace('inPrice-', 'inPrices.');

        if (!isNaN(Number(sorting.column))) {
            sortCol = `updatedDates.externalPrices.${sortCol}`;
        } else if (sortCol.startsWith('inPrices.')) {
            sortCol = `updatedDates.inPrices.${sortCol.substr(sortCol.lastIndexOf('.') + 1)}`;
        }

        return orderBy(
            externalPriceRows,
            [o => toLowerCase(get(o, sortCol)) || nullOrderPlacement],
            sorting.direction
        );
    }
);
